import { useEffect, useState, RefObject, useMemo } from 'react';
import {
  BATCH_CHART_CONFIG,
  LAYOUT_MARGIN
} from '@experiment-management-shared/constants';
import { debounce } from 'lodash';

const { ROW_HEIGHT } = BATCH_CHART_CONFIG;

const SCROLLING_DEBOUNCE = 500;
const DEFAULT_SCROLL_POSITION = {
  top: 0,
  bottom: ROW_HEIGHT * 3 + LAYOUT_MARGIN * 3,
  left: 0,
  right: 0
};

interface UseScrollPositionProps {
  containerRef: RefObject<HTMLDivElement>;
}
const useScrollPositionInBatchChartContainer = ({
  containerRef
}: UseScrollPositionProps) => {
  const [position, setPosition] = useState(DEFAULT_SCROLL_POSITION);

  useEffect(() => {
    const container = containerRef?.current;
    const handleScroll = debounce(() => {
      const container = containerRef?.current?.getBoundingClientRect();

      if (!container) return;

      setPosition({
        top: container.top,
        bottom: container.bottom,
        left: container.left,
        right: container.right
      });
    }, SCROLLING_DEBOUNCE);

    if (container) {
      window.addEventListener('scroll', handleScroll);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const { top, bottom } = useMemo(() => {
    const screenHeight =
      window.innerHeight || document.documentElement.clientHeight;

    // the screen is out of window
    if (position.top > screenHeight || position.bottom < 0) {
      return { top: null, bottom: null };
    }

    // if the container's top is above a screen
    const top = position.top < 0 ? -position.top : 0;
    // if the container's bottom is in a screen
    const bottom =
      position.bottom < screenHeight
        ? -position.top + position.bottom
        : -position.top + screenHeight;

    return {
      top,
      bottom
    };
  }, [position.top, position.bottom]);

  return { top, bottom };
};

export default useScrollPositionInBatchChartContainer;
